import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DEFAULT_CONTENT } from "@/lib/cms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, Upload } from "lucide-react";

const clone = (o) => (typeof structuredClone === "function" ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm text-brown">{label}</Label>{children}</div>;
}

export default function AdminCMS() {
  const { setSettings } = useApp();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const content = r.data.content || clone(DEFAULT_CONTENT);
      // ensure all keys exist
      setC({ ...clone(DEFAULT_CONTENT), ...content });
    });
  }, []);

  const update = (fn) => setC((prev) => { const n = clone(prev); fn(n); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { content: c });
      setSettings(data);
      toast.success("Website content saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const uploadRef = useRef(null);
  const pickImage = (cb) => {
    const input = uploadRef.current;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData(); fd.append("file", file);
      try {
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        cb(r.data.url); toast.success("Image uploaded");
      } catch { toast.error("Upload failed"); }
      input.value = "";
    };
    input.click();
  };

  if (!c) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  const move = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
  };

  return (
    <div className="max-w-4xl">
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" />
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Website Content</h1>
          <p className="text-sm text-brown/60">Edit the storefront without code. Changes go live on save.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="cms-save-btn" className="rounded-full bg-forest hover:bg-earth text-cream shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save & Publish</>}
        </Button>
      </div>

      <Tabs defaultValue="announcement">
        <TabsList className="flex-wrap h-auto bg-sand mb-5">
          <TabsTrigger value="announcement" data-testid="cms-tab-announcement">Announcement</TabsTrigger>
          <TabsTrigger value="header" data-testid="cms-tab-header">Header</TabsTrigger>
          <TabsTrigger value="hero" data-testid="cms-tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="sections" data-testid="cms-tab-sections">Homepage</TabsTrigger>
          <TabsTrigger value="footer" data-testid="cms-tab-footer">Footer</TabsTrigger>
        </TabsList>

        {/* Announcement */}
        <TabsContent value="announcement">
          <Card>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show announcement bar</Label>
              <Switch checked={!!c.announcement.enabled} onCheckedChange={(v) => update((n) => { n.announcement.enabled = v; })} data-testid="cms-ann-enabled" />
            </div>
            <Field label="Announcement text">
              <Input value={c.announcement.text || ""} onChange={(e) => update((n) => { n.announcement.text = e.target.value; })} className="rounded-xl bg-sand" data-testid="cms-ann-text" />
            </Field>
          </Card>
        </TabsContent>

        {/* Header */}
        <TabsContent value="header">
          <Card>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Brand name"><Input value={c.header.brand_name || ""} onChange={(e) => update((n) => { n.header.brand_name = e.target.value; })} className="rounded-xl bg-sand" data-testid="cms-brand-name" /></Field>
              <Field label="Brand tagline"><Input value={c.header.brand_tagline || ""} onChange={(e) => update((n) => { n.header.brand_tagline = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show "Chat on WhatsApp" button</Label>
              <Switch checked={c.header.show_whatsapp_cta !== false} onCheckedChange={(v) => update((n) => { n.header.show_whatsapp_cta = v; })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-sm">Navigation links</Label>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => update((n) => { n.header.nav.push({ label: "New", to: "/" }); })} data-testid="cms-nav-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {c.header.nav.map((item, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={item.label} onChange={(e) => update((n) => { n.header.nav[i].label = e.target.value; })} placeholder="Label" className="rounded-lg bg-sand" />
                    <Input value={item.to} onChange={(e) => update((n) => { n.header.nav[i].to = e.target.value; })} placeholder="/path" className="rounded-lg bg-sand" />
                    <IconBtn onClick={() => update((n) => move(n.header.nav, i, -1))}><ChevronUp className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => update((n) => move(n.header.nav, i, 1))}><ChevronDown className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => update((n) => { n.header.nav.splice(i, 1); })}><Trash2 className="w-4 h-4 text-terracotta" /></IconBtn>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Hero */}
        <TabsContent value="hero">
          <Card>
            <Field label="Badge text"><Input value={c.hero.badge || ""} onChange={(e) => update((n) => { n.hero.badge = e.target.value; })} className="rounded-xl bg-sand" data-testid="cms-hero-badge" /></Field>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Headline line 1"><Input value={c.hero.line1 || ""} onChange={(e) => update((n) => { n.hero.line1 = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Line 2"><Input value={c.hero.line2 || ""} onChange={(e) => update((n) => { n.hero.line2 = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Line 3 (accent)"><Input value={c.hero.line3 || ""} onChange={(e) => update((n) => { n.hero.line3 = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
            <Field label="Subtext"><Textarea value={c.hero.subtext || ""} onChange={(e) => update((n) => { n.hero.subtext = e.target.value; })} className="rounded-xl bg-sand" rows={2} /></Field>
            <Field label="Hero image URL">
              <div className="flex gap-2">
                <Input value={c.hero.image || ""} onChange={(e) => update((n) => { n.hero.image = e.target.value; })} className="rounded-xl bg-sand" data-testid="cms-hero-image" />
                <Button variant="outline" className="rounded-xl shrink-0" onClick={() => pickImage((url) => update((n) => { n.hero.image = url; }))} data-testid="cms-hero-upload"><Upload className="w-4 h-4" /></Button>
              </div>
              {c.hero.image && <img src={c.hero.image} alt="hero preview" className="mt-2 h-28 rounded-xl object-cover" />}
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Primary button label"><Input value={c.hero.primary_cta?.label || ""} onChange={(e) => update((n) => { n.hero.primary_cta.label = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Primary button link"><Input value={c.hero.primary_cta?.to || ""} onChange={(e) => update((n) => { n.hero.primary_cta.to = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Secondary button label"><Input value={c.hero.secondary_cta?.label || ""} onChange={(e) => update((n) => { n.hero.secondary_cta.label = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Secondary button link"><Input value={c.hero.secondary_cta?.to || ""} onChange={(e) => update((n) => { n.hero.secondary_cta.to = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Floating card title"><Input value={c.hero.card_title || ""} onChange={(e) => update((n) => { n.hero.card_title = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Floating card subtitle"><Input value={c.hero.card_subtitle || ""} onChange={(e) => update((n) => { n.hero.card_subtitle = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* Sections */}
        <TabsContent value="sections">
          <p className="text-sm text-brown/60 mb-3">Toggle, rename and reorder homepage sections. Order here = order on the site.</p>
          <div className="space-y-3">
            {c.sections.map((s, i) => (
              <div key={s.id} className="bg-white rounded-2xl soft-shadow p-4" data-testid={`cms-section-${s.id}`}>
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-olive bg-sand px-2 py-0.5 rounded-full">{s.type.replace(/_/g, " ")}</span>
                    <Switch checked={!!s.enabled} onCheckedChange={(v) => update((n) => { n.sections[i].enabled = v; })} data-testid={`cms-section-toggle-${s.id}`} />
                  </div>
                  <div className="flex gap-1">
                    <IconBtn onClick={() => update((n) => move(n.sections, i, -1))}><ChevronUp className="w-4 h-4" /></IconBtn>
                    <IconBtn onClick={() => update((n) => move(n.sections, i, 1))}><ChevronDown className="w-4 h-4" /></IconBtn>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Title"><Input value={s.title || ""} onChange={(e) => update((n) => { n.sections[i].title = e.target.value; })} className="rounded-lg bg-sand" /></Field>
                  <Field label="Subtitle"><Input value={s.subtitle || ""} onChange={(e) => update((n) => { n.sections[i].subtitle = e.target.value; })} className="rounded-lg bg-sand" /></Field>
                </div>
                {s.type === "promo" && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Button label"><Input value={s.cta?.label || ""} onChange={(e) => update((n) => { n.sections[i].cta = { ...(n.sections[i].cta || {}), label: e.target.value }; })} className="rounded-lg bg-sand" /></Field>
                    <Field label="Button link"><Input value={s.cta?.to || ""} onChange={(e) => update((n) => { n.sections[i].cta = { ...(n.sections[i].cta || {}), to: e.target.value }; })} className="rounded-lg bg-sand" /></Field>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Footer */}
        <TabsContent value="footer">
          <Card>
            <Field label="About text"><Textarea value={c.footer.about || ""} onChange={(e) => update((n) => { n.footer.about = e.target.value; })} className="rounded-xl bg-sand" rows={3} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tagline"><Input value={c.footer.tagline || ""} onChange={(e) => update((n) => { n.footer.tagline = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Copyright (use {year})"><Input value={c.footer.copyright || ""} onChange={(e) => update((n) => { n.footer.copyright = e.target.value; })} placeholder="© {year} LIVENEAT Organic..." className="rounded-xl bg-sand" /></Field>
            </div>
            <div className="flex items-center justify-between mt-2"><Label className="text-sm">Footer columns</Label>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => update((n) => { n.footer.columns.push({ title: "New", links: [["Link", "/"]] }); })}><Plus className="w-3.5 h-3.5 mr-1" /> Column</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {c.footer.columns.map((col, ci) => (
                <div key={ci} className="border border-sand rounded-xl p-3 space-y-2">
                  <div className="flex gap-2 items-center">
                    <Input value={col.title} onChange={(e) => update((n) => { n.footer.columns[ci].title = e.target.value; })} className="rounded-lg bg-sand font-500" />
                    <IconBtn onClick={() => update((n) => { n.footer.columns.splice(ci, 1); })}><Trash2 className="w-4 h-4 text-terracotta" /></IconBtn>
                  </div>
                  {col.links.map((lnk, li) => (
                    <div key={li} className="flex gap-1.5 items-center">
                      <Input value={lnk[0]} onChange={(e) => update((n) => { n.footer.columns[ci].links[li][0] = e.target.value; })} placeholder="Label" className="rounded-lg bg-sand h-9 text-sm" />
                      <Input value={lnk[1]} onChange={(e) => update((n) => { n.footer.columns[ci].links[li][1] = e.target.value; })} placeholder="/path" className="rounded-lg bg-sand h-9 text-sm" />
                      <IconBtn onClick={() => update((n) => { n.footer.columns[ci].links.splice(li, 1); })}><Trash2 className="w-3.5 h-3.5 text-terracotta" /></IconBtn>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" className="text-xs text-forest" onClick={() => update((n) => { n.footer.columns[ci].links.push(["Link", "/"]); })}><Plus className="w-3 h-3 mr-1" /> Link</Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl soft-shadow p-6 space-y-4">{children}</div>;
}
function IconBtn({ children, onClick }) {
  return <button type="button" onClick={onClick} className="w-8 h-8 shrink-0 rounded-lg bg-sand hover:bg-sage/30 flex items-center justify-center transition-colors">{children}</button>;
}
